import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Menu,
  X,
  Zap,
  ChevronDown,
  ArrowRight,
  Server,
  Smartphone,
  Globe2,
  LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/portfolio", label: "Portfolio" },
    { href: "/contact", label: "Contact" },
  ];

  const serviceGroups = [
    {
      title: "Odoo & ERP",
      icon: Server,
      color: "bg-indigo-50 text-indigo-600",
      items: [
        { label: "Modules sur mesure", href: "/services/modules-odoo-sur-mesure" },
        { label: "Migration Odoo", href: "/services/migration-odoo" },
        { label: "Audit & conseil", href: "/services/audit-conseil-odoo" },
      ],
    },
    {
      title: "Applications mobiles",
      icon: Smartphone,
      color: "bg-violet-50 text-violet-600",
      items: [
        { label: "Applications iOS & Android", href: "/services/applications-ios-android" },
        { label: "Apps métier & terrain", href: "/services/applications-metier-terrain" },
        { label: "PWA & mode hors ligne", href: "/services/pwa-mode-hors-ligne" },
      ],
    },
    {
      title: "Web & connexions",
      icon: Globe2,
      color: "bg-sky-50 text-sky-600",
      items: [
        { label: "Sites & portails Odoo", href: "/services/sites-portails-odoo" },
        { label: "Intégrations API", href: "/services/integrations-api" },
        { label: "IoT & équipements", href: "/services/iot-equipements-connectes" },
      ],
    },
    {
      title: "Accompagnement",
      icon: LifeBuoy,
      color: "bg-emerald-50 text-emerald-600",
      items: [
        { label: "Formation des équipes", href: "/services/formation-equipes" },
        { label: "Maintenance & support", href: "/services/maintenance-support" },
        { label: "Cadrage de projet", href: "/services/cadrage-projet" },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || servicesOpen || isOpen ? "nova-glass shadow-sm" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="nova-container">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">
              Nova<span className="text-indigo-600">Forge</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${isActive("/") ? "text-indigo-600" : "text-slate-600 hover:text-indigo-600"}`}
            >
              Accueil
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                onClick={() => setServicesOpen(true)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isActive("/services") ? "text-indigo-600" : "text-slate-600 hover:text-indigo-600"
                }`}
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                data-testid="nav-services-menu"
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>

              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5 w-[780px]" data-testid="services-mega-menu">
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-900/15">
                    <div className="flex items-start justify-between gap-8 mb-5 pb-5 border-b border-slate-100">
                      <div>
                        <p className="text-lg font-bold text-slate-900">Un écosystème numérique complet</p>
                        <p className="text-sm text-slate-500 mt-1">Odoo au centre, le web et le mobile connectés autour.</p>
                      </div>
                      <Link to="/services" className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 whitespace-nowrap">
                        Voir tous les services <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-4 gap-5">
                      {serviceGroups.map((group) => (
                        <div key={group.title}>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${group.color}`}>
                              <group.icon className="w-4 h-4" />
                            </span>
                            <p className="text-sm font-bold text-slate-900">{group.title}</p>
                          </div>
                          <div className="space-y-1">
                            {group.items.map((item) => (
                              <Link
                                key={`${group.title}-${item.label}`}
                                to={item.href}
                                className="block rounded-lg px-2 py-2 text-xs leading-snug text-slate-600 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 rounded-xl bg-slate-950 px-5 py-4 flex items-center justify-between gap-5 text-white">
                      <div>
                        <p className="text-sm font-semibold">Vous avez une idée ou un besoin métier ?</p>
                        <p className="text-xs text-slate-400 mt-0.5">Décrivez-le en quelques minutes, nous vous aidons à le cadrer.</p>
                      </div>
                      <Link to="/onboarding">
                        <Button className="bg-indigo-500 hover:bg-indigo-400 text-white whitespace-nowrap">Démarrer mon projet</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href) ? "text-indigo-600" : "text-slate-600 hover:text-indigo-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <Link to="/login" data-testid="nav-login-btn">
              <Button variant="ghost" className="nova-btn-ghost">Connexion</Button>
            </Link>
            <Link to="/onboarding" data-testid="nav-start-project-btn">
              <Button className="nova-btn-primary">Démarrer un projet</Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-slate-600"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden max-h-[calc(100vh-5rem)] overflow-y-auto py-4 border-t border-slate-100" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              <Link to="/" className={`px-4 py-3 text-sm font-medium rounded-lg ${isActive("/") ? "bg-indigo-50 text-indigo-600" : "text-slate-700"}`}>
                Accueil
              </Link>

              <button
                type="button"
                onClick={() => setMobileServicesOpen((open) => !open)}
                className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-900 rounded-lg bg-slate-50"
                aria-expanded={mobileServicesOpen}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>

              {mobileServicesOpen && (
                <div className="px-2 pb-2 space-y-4">
                  {serviceGroups.map((group) => (
                    <div key={group.title} className="rounded-xl border border-slate-100 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${group.color}`}>
                          <group.icon className="w-3.5 h-3.5" />
                        </span>
                        <p className="text-xs font-bold text-slate-900">{group.title}</p>
                      </div>
                      {group.items.map((item) => (
                        <Link key={`${group.title}-${item.label}`} to={item.href} className="block px-2 py-2 text-xs text-slate-600 hover:text-indigo-600">
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                  <Link to="/services" className="flex items-center justify-center gap-2 py-2 text-sm font-semibold text-indigo-600">
                    Voir tous les services <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}

              {navLinks.slice(1).map((link) => (
                <Link key={link.href} to={link.href} className={`px-4 py-3 text-sm font-medium rounded-lg ${isActive(link.href) ? "bg-indigo-50 text-indigo-600" : "text-slate-700"}`}>
                  {link.label}
                </Link>
              ))}

              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-slate-100">
                <Link to="/login"><Button variant="outline" className="w-full">Connexion</Button></Link>
                <Link to="/onboarding"><Button className="w-full nova-btn-primary">Démarrer un projet</Button></Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
